import {
  LayoutDashboard,
  Users,
  Settings,
  Images,
  Gavel,
  WandSparkles,
  Wallet,
  TrendingUp,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ROUTES } from '@/app/routes';
import type { AppRole } from '@/auth/roles';
import { ROUTE_ROLES, type RouteKey } from '@/auth/rbac';

export interface NavChild {
  label: string;
  path: string;
  /** When set, sidebar may show a live count badge for this child. */
  badgeKey?: 'humanReview' | 'frontierReview';
  /**
   * Optional page-permission key for this child. When set, the sidebar filters
   * the child independently (used when siblings map to different RouteKeys).
   */
  pageKey?: RouteKey;
}

export interface NavItem {
  key: RouteKey;
  label: string;
  path: string;
  icon: LucideIcon;
  roles: readonly AppRole[]; // seed / fallback; sidebar prefers effective page keys
  children?: NavChild[] | undefined;
  /**
   * Extra page keys that should keep this parent visible (union with `key`).
   * Used when children map to distinct RouteKeys under one group.
   */
  alsoKeys?: readonly RouteKey[];
}

// Two groups, mirroring CMP navGroups (DrawerPane.kt:67-72). `key` values match ROUTE_ROLES (CONTRACTS.md §8).
export const NAV_GROUPS: NavItem[][] = [
  [
    {
      key: 'dashboard',
      label: 'Dashboard',
      path: ROUTES.dashboard,
      icon: LayoutDashboard,
      roles: ROUTE_ROLES.dashboard,
    },
    {
      key: 'users',
      label: 'User Management',
      path: ROUTES.users,
      icon: Users,
      roles: ROUTE_ROLES.users,
    },
    {
      // Admin List + System & Permissions are system-admin settings — group them
      // like Content/Moderation rather than two sibling top-level rows.
      key: 'admins',
      label: 'System Admin',
      path: ROUTES.admins,
      icon: Settings,
      roles: ROUTE_ROLES.admins,
      alsoKeys: ['system-permissions'],
      children: [
        { label: 'Admin List', path: ROUTES.admins, pageKey: 'admins' },
        {
          label: 'System & Permissions',
          path: ROUTES.systemPermissions,
          pageKey: 'system-permissions',
        },
      ],
    },
    {
      key: 'content',
      label: 'Content Management',
      path: ROUTES.content,
      icon: Images,
      roles: ROUTE_ROLES.content,
      children: [
        { label: 'All Content', path: ROUTES.content },
        { label: 'Human Review', path: ROUTES.humanReview, badgeKey: 'humanReview' },
        {
          label: 'Frontier Review',
          path: ROUTES.frontierReview,
          badgeKey: 'frontierReview',
        },
        { label: 'Auto Reject', path: ROUTES.autoReject },
      ],
    },
    {
      key: 'moderation',
      label: 'Moderation',
      path: ROUTES.moderation,
      icon: Gavel,
      roles: ROUTE_ROLES.moderation,
      children: [
        { label: 'Dashboard', path: ROUTES.moderationDashboard },
        { label: 'User Reports', path: ROUTES.moderationReports },
        { label: 'Pipeline Tests', path: ROUTES.pipelineTestPosts },
        { label: 'Pipeline Lab', path: ROUTES.pipelineLab },
        { label: 'Rules', path: ROUTES.moderationRules },
      ],
    },
    {
      // Video-editor resources. Effects sit UNDER Media rather than beside it:
      // stickers, effects and transitions are all catalogue entries the mobile
      // editor fetches from the same CDN, and only their authoring differs.
      key: 'media',
      label: 'Media',
      path: ROUTES.media,
      icon: WandSparkles,
      roles: ROUTE_ROLES.media,
      // Effects first — it is both the landing target and the daily workflow.
      children: [
        { label: 'Effects', path: ROUTES.effects },
        { label: 'Stickers', path: ROUTES.mediaStickers },
        { label: 'Transitions', path: ROUTES.mediaTransitions },
      ],
    },
  ],
  [
    {
      key: 'finance',
      label: 'Financial Management',
      path: ROUTES.finance,
      icon: Wallet,
      roles: ROUTE_ROLES.finance,
    },
    {
      key: 'engagement',
      label: 'Engagement',
      path: ROUTES.engagement,
      icon: TrendingUp,
      roles: ROUTE_ROLES.engagement,
    },
  ],
];
